import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Crosshair, Link, RefreshCcw, ArrowRight, Database, GitBranch, Globe, BarChart3, Zap, Download, Check } from "lucide-react";
import { USE_CASES, VALUE_PROPS, CHATGPT_COMPARISONS } from "@/lib/constants";

const ICON_MAP = { Crosshair, Link, RefreshCcw };

export function ValueSection() {
  return (
    <section
      data-testid="value-section"
      className="max-w-[820px] mx-auto px-6 pt-10 pb-12"
      style={{ borderTop: "1px solid var(--sdb-border)" }}
    >
      <div className="mb-10">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2" style={{ color: "var(--sdb-text)" }}>
          Not just fake data. Business-coherent data.
        </h2>
        <p className="text-sm" style={{ color: "var(--sdb-text-muted)" }}>
          The difference between a demo that gets "nice prototype" and one that gets a purchase order.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {VALUE_PROPS.map((v, i) => {
          const Icon = ICON_MAP[v.icon];
          return (
            <div
              key={i}
              data-testid={`value-card-${i}`}
              className="p-5 rounded-sm transition-colors"
              style={{
                background: "var(--sdb-surface)",
                border: "1px solid var(--sdb-border)",
              }}
            >
              <Icon className="w-5 h-5 mb-3" style={{ color: "var(--sdb-accent)" }} strokeWidth={1.5} />
              <h3 className="text-sm font-semibold mb-1.5" style={{ color: "var(--sdb-text)" }}>
                {v.title}
              </h3>
              <p className="text-[11px] leading-relaxed" style={{ color: "var(--sdb-text-muted)" }}>
                {v.text}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const CHATGPT_ICON_MAP = { GitBranch, Globe, BarChart3, Zap, Download };

export function WhyNotChatGPTSection() {
  return (
    <section
      data-testid="why-not-chatgpt-section"
      className="max-w-[820px] mx-auto px-6 pt-10 pb-12"
      style={{ borderTop: "1px solid var(--sdb-border)" }}
    >
      <div className="mb-10">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2" style={{ color: "var(--sdb-text)" }}>
          Why not just ask ChatGPT?
        </h2>
        <p className="text-sm" style={{ color: "var(--sdb-text-muted)" }}>
          LLMs write great poems but terrible foreign keys. Here is why you need a specialized engine.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CHATGPT_COMPARISONS.map((v, i) => {
          const Icon = CHATGPT_ICON_MAP[v.icon];
          return (
            <div
              key={i}
              data-testid={`comparison-card-${i}`}
              className="p-5 rounded-sm transition-colors"
              style={{
                background: "var(--sdb-surface)",
                border: "1px solid var(--sdb-border)",
              }}
            >
              <Icon className="w-5 h-5 mb-3" style={{ color: "var(--sdb-accent)" }} strokeWidth={1.5} />
              <h3 className="text-sm font-semibold mb-1.5" style={{ color: "var(--sdb-text)" }}>
                {v.title}
              </h3>
              <p className="text-[11px] leading-relaxed" style={{ color: "var(--sdb-text-muted)" }}>
                {v.text}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function UseCasesSection() {
  return (
    <section
      data-testid="usecases-section"
      className="px-6 py-10 text-center"
      style={{ borderTop: "1px solid var(--sdb-border)" }}
    >
      <p
        className="font-mono text-[10px] tracking-[2px] uppercase mb-5"
        style={{ color: "var(--sdb-text-muted)" }}
      >
        Built for every AI application vertical
      </p>
      <div className="flex flex-wrap justify-center gap-2 max-w-[700px] mx-auto">
        {USE_CASES.map((u, i) => (
          <Badge
            key={i}
            variant="outline"
            className="rounded-none font-mono text-[10px] px-3 py-1 transition-colors"
            style={{
              borderColor: "var(--sdb-border)",
              color: "var(--sdb-text-muted)",
            }}
          >
            {u}
          </Badge>
        ))}
      </div>
    </section>
  );
}

const FREE_FEATURES = [
  "3 database generations",
  "5+ relational tables per DB",
  "1,000+ rows per generation",
  "CSV, JSON, SQL exports",
];

const PAYG_FEATURES = [
  "Everything in Free",
  "Unlimited generations",
  "Priority model selection",
  "Buy credits anytime ($5 min)",
];

export function PricingSection({ onSignUp }) {
  return (
    <section
      data-testid="pricing-section"
      className="max-w-[820px] mx-auto px-6 pt-10 pb-12"
      style={{ borderTop: "1px solid var(--sdb-border)" }}
    >
      <div className="mb-10">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2" style={{ color: "var(--sdb-text)" }}>
          Simple, transparent pricing
        </h2>
        <p className="text-sm" style={{ color: "var(--sdb-text-muted)" }}>
          Start free. Pay only when you need more.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Free Card */}
        <div
          className="p-5 rounded-sm transition-colors flex flex-col"
          style={{
            background: "var(--sdb-surface)",
            border: "1px solid var(--sdb-border)",
          }}
        >
          <p className="font-mono text-[10px] tracking-[2px] uppercase mb-3" style={{ color: "var(--sdb-accent)" }}>
            FREE
          </p>
          <p className="text-3xl font-semibold mb-4" style={{ color: "var(--sdb-text)" }}>
            $0
          </p>
          <div className="flex-1">
            {FREE_FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--sdb-accent)" }} strokeWidth={2} />
                <span className="font-mono text-[11px]" style={{ color: "var(--sdb-text-muted)" }}>{f}</span>
              </div>
            ))}
          </div>
          <Button
            data-testid="pricing-free-btn"
            onClick={onSignUp}
            className="gradient-bg text-black font-semibold text-sm rounded-sm px-6 py-2.5 h-auto glow-btn hover:text-black w-full mt-4"
          >
            Get Started — Free
          </Button>
        </div>

        {/* Pay As You Go Card */}
        <div
          className="p-5 rounded-sm transition-colors flex flex-col"
          style={{
            background: "var(--sdb-surface)",
            border: "1px solid var(--sdb-accent)",
            boxShadow: "0 0 20px rgba(168,162,255,0.08)",
          }}
        >
          <p className="font-mono text-[10px] tracking-[2px] uppercase mb-3" style={{ color: "var(--sdb-accent)" }}>
            PAY AS YOU GO
          </p>
          <p className="text-3xl font-semibold mb-1" style={{ color: "var(--sdb-text)" }}>
            from $0.20
          </p>
          <p className="font-mono text-[10px] mb-4" style={{ color: "var(--sdb-text-muted)" }}>
            /generation
          </p>
          <div className="flex-1">
            {PAYG_FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--sdb-accent)" }} strokeWidth={2} />
                <span className="font-mono text-[11px]" style={{ color: "var(--sdb-text-muted)" }}>{f}</span>
              </div>
            ))}
          </div>
          <Button
            data-testid="pricing-credits-btn"
            onClick={onSignUp}
            variant="outline"
            className="font-semibold text-sm rounded-sm px-6 py-2.5 h-auto w-full mt-4"
            style={{ borderColor: "var(--sdb-accent)", color: "var(--sdb-accent)" }}
          >
            Buy Credits
          </Button>
        </div>
      </div>
    </section>
  );
}

export function BottomCTA({ onSignUp }) {
  return (
    <section
      data-testid="bottom-cta"
      className="px-6 py-16 text-center"
      style={{ borderTop: "1px solid var(--sdb-border)" }}
    >
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3" style={{ color: "var(--sdb-text)" }}>
        Stop hand-crafting demo databases.
      </h2>
      <p className="text-sm max-w-[480px] mx-auto mb-8" style={{ color: "var(--sdb-text-muted)" }}>
        Join AI teams who are shipping demos in hours, not weeks.
      </p>
      <Button
        data-testid="bottom-cta-btn"
        onClick={onSignUp}
        className="gradient-bg text-black font-semibold text-sm rounded-sm px-8 py-3 h-auto glow-btn hover:text-black"
      >
        Start Generating — Free
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
      <p className="font-mono text-[10px] mt-3" style={{ color: "var(--sdb-text-subtle)" }}>
        3 free generations. No credit card required.
      </p>
    </section>
  );
}

export function Footer() {
  return (
    <footer
      data-testid="footer"
      className="px-6 py-5 flex justify-between items-center flex-wrap gap-2"
      style={{ borderTop: "1px solid var(--sdb-border)" }}
    >
      <span className="text-[11px] flex items-center gap-1.5" style={{ color: "var(--sdb-text-muted)" }}>
        <Database className="w-3 h-3" />
        2025 Arkya MockDB
      </span>
      <span className="font-mono text-[10px]" style={{ color: "var(--sdb-text-subtle)" }}>
        Powered by Gemini 3
      </span>
    </footer>
  );
}
